import { computed, inject } from "vue";
import { Store, storeKey, useStore } from "vuex";
import { getProperty } from "@/lib/helpers";

/**
 * Reads a tenant feature flag from the provided Vuex store, fetching the tenant
 * when it has not been loaded yet. Requires a store: a component without one
 * should use `useOptionalFeatureFlag`.
 */
export function useFeatureFlag(feature: string) {
  const store = useStore();

  if (!store.state.tenant && !store.state.fetchingTenant) {
    store.dispatch("fetchTenant");
  }

  const featureEnabled = computed(() => {
    if (!getProperty(store.state.tenant, "feature_flags") || !feature) {
      return false;
    } else {
      return store.state.tenant.feature_flags[feature];
    }
  });
  return featureEnabled;
}

interface TenantFlagsState {
  tenant?: { feature_flags?: Record<string, unknown> } | null;
}

/**
 * Reads a tenant feature flag when a store happens to be provided and reports
 * the flag off when none is. For shared components that also mount without
 * Vuex (Storybook, isolated specs, legacy hosts): the read never throws and
 * never triggers a tenant fetch. The tenant payload omits a disabled flag's
 * key entirely, so anything other than `true` means off.
 */
export function useOptionalFeatureFlag(feature: string) {
  const store = inject<Store<TenantFlagsState> | null>(storeKey, null);

  return computed(() => store?.state.tenant?.feature_flags?.[feature] === true);
}
